/**
 * Swirl Evolution Chart Component
 * Visual timeline of how a Swirl's intensity and tags changed over time
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Scatter
} from 'recharts';
import { format, parseISO } from 'date-fns';

export default function SwirlEvolutionChart({ evolutionData }) {
  // Transform data for chart
  const chartData = useMemo(() => {
    if (!evolutionData || evolutionData.length === 0) {
      return [];
    }

    let cumulativeItems = 0;
    
    return evolutionData.map((week, index) => {
      cumulativeItems += week.new_items || 0;
      
      return {
        week: format(parseISO(week.week_start), 'MMM d'),
        weekFull: week.week_start,
        newItems: week.new_items || 0,
        totalItems: cumulativeItems,
        avgSimilarity: (week.avg_similarity || 0) * 100,
        dominantTags: week.dominant_tags || [],
        avgColor: week.avg_color || '#A78BFA',
        index,
      };
    });
  }, [evolutionData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-4 border border-neutral-200">
          <p className="font-semibold text-neutral-800 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            Week of {data.weekFull ? format(parseISO(data.weekFull), 'MMMM d, yyyy') : data.week}
          </p>
          <p className="text-sm text-neutral-600 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
            <strong>{data.newItems}</strong> new items added
          </p>
          {data.dominantTags && data.dominantTags.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-neutral-500 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                Top themes:
              </p>
              <div className="flex flex-wrap gap-1">
                {data.dominantTags.slice(0, 3).map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-neutral-200 p-8 text-center">
        <p className="text-neutral-500" style={{ fontFamily: 'Inter, sans-serif' }}>
          Evolution data will appear as this Swirl grows over time.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white/60 backdrop-blur-sm rounded-xl border border-neutral-200 p-6"
    >
      <h3 className="text-lg font-semibold text-neutral-800 mb-4" style={{ 
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        opacity: 0.9
      }}>
        Growth Timeline
      </h3>
      
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
          <XAxis
            dataKey="week"
            stroke="#9CA3AF"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
          />
          <YAxis
            yAxisId="left"
            stroke="#9CA3AF"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
            label={{ value: 'Items', angle: -90, position: 'insideLeft', style: { fontFamily: 'Inter, sans-serif' } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#9CA3AF"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
            label={{ value: 'Similarity %', angle: 90, position: 'insideRight', style: { fontFamily: 'Inter, sans-serif' } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="totalItems"
            stroke="#A78BFA"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Scatter
            yAxisId="right"
            dataKey="avgSimilarity"
            fill="#C084FC"
            shape={(props) => {
              const { cx, cy, payload } = props;
              const radius = Math.max(4, (payload.avgSimilarity / 100) * 10);
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={radius}
                  fill={payload.avgColor || '#A78BFA'}
                  opacity={0.7}
                />
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 text-xs text-neutral-600" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <span>Total items</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-400"></div>
          <span>Similarity score</span>
        </div>
      </div>
    </motion.div>
  );
}

